/**
 * V55.22: 좌석배치도 팔레트 컴포넌트
 *
 * 편집 모드에서 오브젝트 추가/삭제 기능을 제공합니다.
 * V55.22: floor, textBox 오브젝트 추가
 *
 * @module pages/organization/components/seatMap/SeatMapPalette
 */

import React from 'react';
import {
  User,
  Printer,
  Users,
  Coffee,
  Package,
  Square,
  Minus,
  Trash2,
  Plus,
  LayoutGrid,  // V52.2: Zone 아이콘
  Layers,      // V55.22: Floor 아이콘
  Type,        // V55.22: TextBox 아이콘
} from 'lucide-react';

import {
  SeatMapObjectType,
  SEAT_MAP_OBJECT_CONFIGS,
} from '../../../../shared/types/seatMap';

// ============================================
// 아이콘 매핑
// ============================================

/**
 * V52.2: zone 아이콘 추가
 * V55.22: floor, textBox 아이콘 추가
 */
const iconMap: Record<SeatMapObjectType, React.ComponentType<{ size?: number }>> = {
  floor: Layers,      // V55.22
  zone: LayoutGrid,   // V52.2
  seat: User,
  printer: Printer,
  meeting_room: Users,
  kitchen: Coffee,
  storage: Package,
  desk: Square,
  partition: Minus,
  textBox: Type,      // V55.22
};

// ============================================
// Props 타입
// ============================================

interface SeatMapPaletteProps {
  /** 오브젝트 추가 핸들러 */
  onAddObject: (type: SeatMapObjectType) => void;

  /** 선택된 오브젝트 ID */
  selectedObjectId: string | null;

  /** 오브젝트 삭제 핸들러 */
  onDeleteObject: (objectId: string) => void;
}

// ============================================
// 오브젝트 타입 목록
// ============================================

/**
 * V52.2: zone 추가 (맨 앞에 배치 - 배경 요소)
 * V55.22: floor (최하위 레이어), textBox 추가
 */
const objectTypes: SeatMapObjectType[] = [
  'floor',    // V55.22: 바닥 (최하위 레이어)
  'zone',     // V52.2: 구역 (배경)
  'textBox',  // V55.22: 텍스트 박스
  'seat',
  'desk',
  'printer',
  'meeting_room',
  'kitchen',
  'storage',
  'partition',
];

// ============================================
// 팔레트 컴포넌트
// ============================================

export function SeatMapPalette({
  onAddObject,
  selectedObjectId,
  onDeleteObject,
}: SeatMapPaletteProps) {
  return (
    <div
      style={{
        width: '180px',
        background: 'white',
        borderRight: '1px solid var(--color-gray-200)',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        overflowY: 'auto',
      }}
    >
      {/* 오브젝트 추가 섹션 */}
      <div>
        <div
          style={{
            fontSize: '12px',
            fontWeight: '600',
            color: '#64748b',
            marginBottom: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          <Plus size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
          오브젝트 추가
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {objectTypes.map((type) => {
            const config = SEAT_MAP_OBJECT_CONFIGS[type];
            const IconComponent = iconMap[type];

            return (
              <button
                key={type}
                onClick={() => onAddObject(type)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f0f9ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    background: '#f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <IconComponent size={18} />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#1e293b',
                    }}
                  >
                    {config.label}
                  </div>
                  <div
                    style={{
                      fontSize: '11px',
                      color: '#94a3b8',
                    }}
                  >
                    {config.defaultWidth}×{config.defaultHeight}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 선택된 오브젝트 작업 */}
      {selectedObjectId && (
        <div
          style={{
            borderTop: '1px solid var(--color-gray-200)',
            paddingTop: '16px',
          }}
        >
          <div
            style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#64748b',
              marginBottom: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            선택된 오브젝트
          </div>

          <button
            onClick={() => onDeleteObject(selectedObjectId)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              background: '#fef2f2',
              color: '#dc2626',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#fee2e2';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fef2f2';
            }}
          >
            <Trash2 size={16} />
            삭제
          </button>
        </div>
      )}

      {/* 도움말 */}
      <div
        style={{
          marginTop: 'auto',
          padding: '12px',
          background: '#f8fafc',
          borderRadius: '8px',
          fontSize: '11px',
          color: '#64748b',
          lineHeight: '1.5',
        }}
      >
        <strong>Tip:</strong> 오브젝트를 드래그하여 위치를 조정하세요.
        좌석을 클릭하면 직원을 지정할 수 있습니다.
      </div>
    </div>
  );
}
